import React, { useState, useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import SelectorSection from "./components/SelectorSection";
import AboutSection from "./components/AboutSection";
import EcosystemSection from "./components/EcosystemSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import AccessibilityWidget from "./components/AccessibilityWidget";
import WhatsAppFloatingButton from "./components/WhatsAppFloatingButton";
import HackathonPopup from "./components/HackathonPopup";
import SEO from "./components/SEO";
import { OrganizationSchema } from "./components/StructuredData";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "sonner";

/** Global floating action widgets — hidden on the community chat route
 *  where they overlap the message thread and compose area. */
const HIDE_FABS_ON = [/^\/members\/chat/];
const GlobalFabs = () => {
  const { pathname } = useLocation();
  if (HIDE_FABS_ON.some((r) => r.test(pathname))) return null;
  return (
    <>
      <AccessibilityWidget />
      <WhatsAppFloatingButton />
    </>
  );
};

// ─── Route-level code-splitting ─────────────────────────────────────
// All pages are lazy-loaded. The initial bundle only contains the Home
// route + global components, dropping first-load JS by ~70-90%.
// React will fetch each page's chunk on demand (when its route is hit).
// See <Suspense fallback={<PageLoader />}> below the <Routes>.

// Landing Pages
const AIAgencyPage = lazy(() => import("./pages/AIAgencyPage"));
const PartnerTrackPage = lazy(() => import("./pages/PartnerTrackPage"));
const OPMPage = lazy(() => import("./pages/OPMPage"));
const ExecutivesPage = lazy(() => import("./pages/ExecutivesPage"));

// Affiliate Masters Pages
const MerchantsPage = lazy(() => import("./pages/MerchantsPage"));
const CPAMasterPage = lazy(() => import("./pages/CPAMasterPage"));
const AffiliateClubPage = lazy(() => import("./pages/AffiliateClubPage"));
const ProfitCalculatorPage = lazy(() => import("./pages/ProfitCalculatorPage"));
const JoinClubPage = lazy(() => import("./pages/JoinClubPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const PPCRebelPage = lazy(() => import("./pages/PPCRebelPage"));

// Training Program Pages
const TrainingProgramPage = lazy(() => import("./pages/TrainingProgramPage"));
const TrainingQuestionnairePage = lazy(() => import("./pages/TrainingQuestionnairePage"));

// Lead Magnet Pages
const AffiliateBusinessPage = lazy(() => import("./pages/AffiliateBusinessPage"));
const BlackBoxPage = lazy(() => import("./pages/BlackBoxPage"));

// Events
const HackathonPage = lazy(() => import("./pages/HackathonPage"));

// Club Questionnaire
const ClubQuestionnairePage = lazy(() => import("./pages/ClubQuestionnairePage"));

// AI Calculator Funnel
const AICalculatorFunnelPage = lazy(() => import("./pages/AICalculatorFunnelPage"));

// Blog
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const NewsCategoryPage = lazy(() => import("./pages/NewsCategoryPage"));
const NewsArticlePage = lazy(() => import("./pages/NewsArticlePage"));
const OffersIndexPage = lazy(() => import("./pages/OffersIndexPage"));
const OfferLandingPage = lazy(() => import("./pages/OfferLandingPage"));
const DynamicPage = lazy(() => import("./pages/DynamicPage"));

// Admin (heavy — includes TipTap, Chart.js, etc.)
const AdminPage = lazy(() => import("./pages/AdminPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage"));

// Members Area (Phase 2 — trial + skeleton)
const TrialSignupPage = lazy(() => import("./pages/TrialSignupPage"));
const MagicLinkConsumePage = lazy(() => import("./pages/MagicLinkConsumePage"));
const PaymentCompletePage = lazy(() => import("./pages/PaymentCompletePage"));
const MembersHome = lazy(() => import("./pages/members/MembersHome"));
const CoursesCatalog = lazy(() => import("./pages/members/CoursesCatalog"));
const LibraryPage = lazy(() => import("./pages/members/LibraryPage"));
const ChatPage = lazy(() => import("./pages/members/ChatPage"));
const TicketsPage = lazy(() => import("./pages/members/TicketsPage"));
const TicketDetailPage = lazy(() => import("./pages/members/TicketDetailPage"));
const LearnPage = lazy(() => import("./pages/members/LearnPage"));
const BillingPage = lazy(() => import("./pages/members/BillingPage"));
const NotificationsPage = lazy(() => import("./pages/members/NotificationsPage"));
const PaymentCallbackPage = lazy(() => import("./pages/PaymentCallbackPage"));
const CourseDetailPage = lazy(() => import("./pages/CourseDetailPage"));
const BundleDetailPage = lazy(() => import("./pages/BundleDetailPage"));

// Tracking
import PageTracker from "./components/PageTracker";

// Route prefetch
import { prefetchRoute, attachGlobalPrefetch } from "./lib/route_prefetch";

// Legal Pages
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));

// ── Register prefetch factories (same import functions as the lazy refs).
// Triggers chunk download on hover/touchstart via prefetchRoute.fetch(path)
// or on idle via prefetchRoute.prime(paths) — used below to warm up the
// top-converting funnel pages right after Home loads.
prefetchRoute.register("/ai-agency", () => import("./pages/AIAgencyPage"));
prefetchRoute.register("/partner-track", () => import("./pages/PartnerTrackPage"));
prefetchRoute.register("/opm", () => import("./pages/OPMPage"));
prefetchRoute.register("/executives", () => import("./pages/ExecutivesPage"));
prefetchRoute.register("/merchants", () => import("./pages/MerchantsPage"));
prefetchRoute.register("/cpa-master", () => import("./pages/CPAMasterPage"));
prefetchRoute.register("/affiliate-club", () => import("./pages/AffiliateClubPage"));
prefetchRoute.register("/profit-calculator", () => import("./pages/ProfitCalculatorPage"));
prefetchRoute.register("/join-club", () => import("./pages/JoinClubPage"));
prefetchRoute.register("/events", () => import("./pages/EventsPage"));
prefetchRoute.register("/ppc-rebel", () => import("./pages/PPCRebelPage"));
prefetchRoute.register("/training-program", () => import("./pages/TrainingProgramPage"));
prefetchRoute.register("/training-questionnaire", () => import("./pages/TrainingQuestionnairePage"));
prefetchRoute.register("/affiliate-business", () => import("./pages/AffiliateBusinessPage"));
prefetchRoute.register("/black-box", () => import("./pages/BlackBoxPage"));
prefetchRoute.register("/hackathon", () => import("./pages/HackathonPage"));
prefetchRoute.register("/live-workshop-june-2026", () => import("./pages/HackathonPage"));
prefetchRoute.register("/club-questionnaire", () => import("./pages/ClubQuestionnairePage"));
prefetchRoute.register("/ai-calculator", () => import("./pages/AICalculatorFunnelPage"));
prefetchRoute.register("/blog", () => import("./pages/BlogPage"));
prefetchRoute.register("/blog/news", () => import("./pages/NewsCategoryPage"));
// Dynamic article routes share the same page chunk — register synthetic keys
// so listings can prefetch the article chunk on idle/hover.
prefetchRoute.register("/blog/news/*", () => import("./pages/NewsArticlePage"));
prefetchRoute.register("/blog/*", () => import("./pages/BlogPostPage"));

// Members area — highest hover-hit-rate on the logged-in nav
prefetchRoute.register("/members", () => import("./pages/members/MembersHome"));
prefetchRoute.register("/members/home", () => import("./pages/members/MembersHome"));
prefetchRoute.register("/members/courses", () => import("./pages/members/CoursesCatalog"));
prefetchRoute.register("/members/chat", () => import("./pages/members/ChatPage"));
prefetchRoute.register("/members/tickets", () => import("./pages/members/TicketsPage"));
prefetchRoute.register("/members/tickets/*", () => import("./pages/members/TicketDetailPage"));
prefetchRoute.register("/members/learn", () => import("./pages/members/LearnPage"));
prefetchRoute.register("/members/billing", () => import("./pages/members/BillingPage"));
prefetchRoute.register("/members/notifications", () => import("./pages/members/NotificationsPage"));
prefetchRoute.register("/courses/*", () => import("./pages/CourseDetailPage"));
prefetchRoute.register("/bundles/*", () => import("./pages/BundleDetailPage"));

// Auth surface — small chunks but very likely follow-up hop from Home
prefetchRoute.register("/login", () => import("./pages/LoginPage"));
prefetchRoute.register("/trial", () => import("./pages/TrialSignupPage"));

// Minimal loader during route chunk download — Hebrew RTL safe
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FDF8FA]" data-testid="page-loader">
    <div className="w-10 h-10 border-4 border-[#C5366A]/20 border-t-[#C5366A] rounded-full animate-spin" aria-label="טוען" />
  </div>
);

// Skip to main content link for keyboard accessibility
const SkipLink = () => (
  <a
    href="#main-content"
    className="skip-link"
    tabIndex={0}
  >
    דלג לתוכן הראשי
  </a>
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MeetingPopup = ({ meetingDate, meetingTitle, onClose }) => {
  if (!meetingDate) return null;

  const formatMeetingDate = (isoStr) => {
    try {
      const [datePart, timePart] = isoStr.split('T');
      const [y, m, d] = datePart.split('-');
      const [hr, min] = (timePart || '20:00').split(':');
      const dayNames = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
      const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
      const tmpDate = new Date(Number(y), Number(m) - 1, Number(d));
      return `יום ${dayNames[tmpDate.getDay()]}, ${Number(d)} ${monthNames[Number(m) - 1]} ${y}, שעה ${hr}:${min}`;
    } catch {
      return isoStr;
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="meeting-popup-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 md:p-8 animate-in fade-in zoom-in-95 duration-300"
        dir="rtl"
        data-testid="meeting-popup"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500 hover:text-gray-700"
          data-testid="meeting-popup-close"
        >
          ✕
        </button>

        {/* Content */}
        <div className="text-center">
          <div className="w-14 h-14 bg-[#C5366A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-[#C5366A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>

          <h2 className="text-2xl font-black text-[#2D2D2D] mb-2" data-testid="popup-meeting-title">
            {meetingTitle || 'מפגש LIVE בזום'}
          </h2>

          <p className="text-[#6B6B6B] mb-4">
            ההדרכה הסגורה הקרובה של "מרד הפיפיסיסטים"
          </p>

          <div className="bg-[#FDF8FA] border border-[#C5366A]/20 rounded-xl p-4 mb-5">
            <p className="text-[#C5366A] font-bold text-lg" data-testid="popup-meeting-date">
              {formatMeetingDate(meetingDate)}
            </p>
            <p className="text-[#6B6B6B] text-sm mt-1">שעון ירושלים</p>
          </div>

          <a
            href="/ppc-rebel"
            className="inline-flex items-center justify-center gap-2 bg-[#C5366A] text-white px-8 py-3.5 rounded-xl font-bold text-base hover:bg-[#A12D56] transition-all duration-300 shadow-lg shadow-[#C5366A]/20 w-full"
            data-testid="popup-cta-btn"
          >
            שמרו לי מקום
            <svg className="w-5 h-5 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>

          <button
            onClick={onClose}
            className="mt-3 text-gray-400 text-sm hover:text-gray-600 transition-colors"
            data-testid="popup-dismiss-btn"
          >
            לא עכשיו, תודה
          </button>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [meetingDate, setMeetingDate] = useState(null);
  const [meetingTitle, setMeetingTitle] = useState('');

  // Prefetch the top-converting funnel pages during idle time after Home mounts.
  // Result: clicks from the Home page to /join-club, /black-box, /ppc-rebel
  // feel instant because their chunks are already cached by the browser.
  useEffect(() => {
    prefetchRoute.prime([
      '/join-club',
      '/black-box',
      '/ppc-rebel',
      '/affiliate-business',
      '/blog',
    ]);
  }, []);

  useEffect(() => {
    const seen = localStorage.getItem('meeting_popup_seen');
    if (seen) return;

    fetch(`${API_URL}/api/config/meeting-date`)
      .then(r => r.json())
      .then(data => {
        if (data.meeting_date) {
          // Check if meeting is still in the future
          const nowJerusalem = new Date(
            new Date().toLocaleString('en-US', { timeZone: 'Asia/Jerusalem' })
          );
          const target = new Date(data.meeting_date);
          if (target.getTime() > nowJerusalem.getTime()) {
            setMeetingDate(data.meeting_date);
            if (data.meeting_title) setMeetingTitle(data.meeting_title);
            // Small delay so the page renders first
            setTimeout(() => setShowPopup(true), 1500);
          }
        }
      })
      .catch(() => {});
  }, []);

  const handleClosePopup = () => {
    setShowPopup(false);
    localStorage.setItem('meeting_popup_seen', Date.now().toString());
  };

  return (
    <div className="min-h-screen bg-[#FDF8FA]">
      <SEO
        title="המרכז הישראלי לעצמאות דיגיטלית"
        description="הפכו את הידע שלכם בשיווק דיגיטלי לנכסים מניבים. מועדון משווקים מקצועי, הצעות אפיליאייט בלעדיות, וליווי אישי לבניית הכנסה עצמאית."
        path="/"
      />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <HeroSection />
        <SelectorSection />
        <AboutSection />
        <EcosystemSection />
        <ContactSection />
      </main>
      <Footer />
      {showPopup && <MeetingPopup meetingDate={meetingDate} meetingTitle={meetingTitle} onClose={handleClosePopup} />}
    </div>
  );
};

function App() {
  // Attach a single global mouseover/focus/touchstart listener that triggers
  // route prefetching on hover for every <a>/<Link> on the site — no per-
  // component instrumentation needed. Warms up top routes on first idle.
  useEffect(() => {
    const detach = attachGlobalPrefetch();
    const idle = 'requestIdleCallback' in window
      ? window.requestIdleCallback(() => prefetchRoute.prime([
          '/login', '/members', '/members/chat', '/members/courses', '/affiliate-club',
        ]), { timeout: 3000 })
      : window.setTimeout(() => prefetchRoute.prime([
          '/login', '/members', '/members/chat', '/members/courses', '/affiliate-club',
        ]), 1500);
    return () => {
      detach();
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(idle);
      else clearTimeout(idle);
    };
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
      <AuthProvider>
      <OrganizationSchema />
      <BrowserRouter>
        <PageTracker />
        <SkipLink />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/ai-agency" element={<AIAgencyPage />} />
            <Route path="/partner-track" element={<PartnerTrackPage />} />
            <Route path="/opm" element={<OPMPage />} />
            <Route path="/executives" element={<ExecutivesPage />} />
            {/* Affiliate Masters Funnel */}
            <Route path="/merchants" element={<MerchantsPage />} />
            <Route path="/cpa-master" element={<CPAMasterPage />} />
            <Route path="/affiliate-club" element={<AffiliateClubPage />} />
            <Route path="/profit-calculator" element={<ProfitCalculatorPage />} />
            <Route path="/join-club" element={<JoinClubPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/offers" element={<OffersIndexPage />} />
            <Route path="/offers/:slug" element={<OfferLandingPage />} />
            <Route path="/p/:slug" element={<DynamicPage />} />
            <Route path="/ppc-rebel" element={<PPCRebelPage />} />
            {/* Training Program */}
            <Route path="/training-program" element={<TrainingProgramPage />} />
            <Route path="/training-questionnaire" element={<TrainingQuestionnairePage />} />
            <Route path="/affiliate-business" element={<AffiliateBusinessPage />} />
            <Route path="/black-box" element={<BlackBoxPage />} />
            <Route path="/hackathon" element={<HackathonPage />} />
            <Route path="/live-workshop-june-2026" element={<HackathonPage />} />
            <Route path="/hackathon-test" element={<HackathonPage />} />
            <Route path="/club-questionnaire" element={<ClubQuestionnairePage />} />
            <Route path="/ai-calculator" element={<AICalculatorFunnelPage />} />
            {/* Blog */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/news" element={<NewsCategoryPage />} />
            <Route path="/blog/news/:slug" element={<NewsArticlePage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/magic" element={<MagicLinkConsumePage />} />
            <Route path="/payment-complete" element={<PaymentCompletePage />} />
            {/* Trial + Members Area */}
            <Route path="/trial-signup" element={<TrialSignupPage />} />
            <Route path="/members" element={<MembersHome />} />
            <Route path="/members/courses" element={<CoursesCatalog />} />
            <Route path="/members/library" element={<LibraryPage />} />
            <Route path="/members/learn/:slug/:lessonId" element={<LearnPage />} />
            <Route path="/members/chat" element={<ChatPage />} />
            <Route path="/members/tickets" element={<TicketsPage />} />
            <Route path="/members/tickets/:id" element={<TicketDetailPage />} />
            <Route path="/members/billing" element={<BillingPage />} />
            <Route path="/members/notifications" element={<NotificationsPage />} />
            <Route path="/payment/success" element={<PaymentCallbackPage variant="success" />} />
            <Route path="/payment/failure" element={<PaymentCallbackPage variant="failure" />} />
            <Route path="/payment/cancel" element={<PaymentCallbackPage variant="cancel" />} />
            <Route path="/course/:slug" element={<CourseDetailPage />} />
            <Route path="/bundle/:slug" element={<BundleDetailPage />} />
            {/* Legal Pages */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
          </Routes>
        </Suspense>
        {/* Global Components */}
        <CookieConsent />
        <GlobalFabs />
        <HackathonPopup />
        {/* Sonner toaster — mounted once globally so any page (esp. the
            course checkout modal that fires "התשלום התקבל") can toast
            without redeclaring a provider. RTL-aware via richColors. */}
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
      </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
